type TestId = string;
import { Close, Eye, EyeOff } from '@/icons/svg';

import {
    ChangeEvent,
    cloneElement,
    FocusEvent,
    forwardRef,
    InputHTMLAttributes,
    isValidElement,
    ReactElement,
    ReactNode,
    Ref,
    useId,
    useState,
} from 'react';
import { useNoAutocomplete, withSemanticEventName } from './useNoAutocomplete';

export type InputSize = 'large' | 'medium' | 'small' | 'xsmall';
export type InputState = 'default' | 'hover' | 'filled' | 'disabled' | 'error';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'value' | 'type'> {
    size?: InputSize;
    state?: InputState;
    label?: string;
    errorText?: string;
    errorInline?: boolean;
    сlassName?: string;
    value?: string;
    inputRef?: Ref<HTMLInputElement>;
    icon?: ReactNode; // новый пропс для иконки
    type?: 'text' | 'password' | 'email' | 'date';
    clearable?: boolean;
    onClear?: () => void;
    showCharCount?: boolean;
    required?: boolean;
    testId?: TestId;
    clearButtonTestId?: TestId;
    passwordToggleTestId?: TestId;
    allowAutocomplete?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
    (
        {
            size = 'medium',
            state = 'default',
            label,
            errorText,
            errorInline = false,
            className = 'text-primary',
            value,
            disabled = false,
            inputRef,
            icon, // иконка из пропсов
            type,
            clearable = false,
            onClear,
            showCharCount = false,
            required: requiredProp,
            testId,
            clearButtonTestId,
            passwordToggleTestId,
            allowAutocomplete = false,
            ...props
        },
        ref,
    ) => {
        const isControlled = value !== undefined;
        const [internalValue, setInternalValue] = useState(value ?? '');
        const displayValue = isControlled ? value : internalValue;
        const [showPassword, setShowPassword] = useState(false);
        const generatedId = useId();
        const inputId = props.id ?? generatedId;
        const noAutocomplete = useNoAutocomplete();

        let currentState = state;
        if (disabled) {
            currentState = 'disabled';
        } else if (state === 'error') {
            currentState = 'error';
        } else if (displayValue) {
            currentState = 'filled';
        }

        const sizeStyles = {
            large: 'h-10 px-4',
            medium: 'h-8 px-3',
            small: 'h-7 px-3',
            xsmall: 'h-6 px-2',
        };

        const stateStyles = {
            default: `bg-white ${className}`,
            hover: `bg-white ${className}`,
            filled: `bg-white ${className}`,
            disabled: 'bg-white text-neutral-500 cursor-not-allowed',
            error: `border-error bg-white ${className}`,
        };

        const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
            withSemanticEventName(e.target, props.name, () => props.onFocus?.(e));
        };

        const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
            withSemanticEventName(e.target, props.name, () => props.onBlur?.(e));
        };

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            if (!isControlled) setInternalValue(e.target.value);
            withSemanticEventName(e.target, props.name, () => props.onChange?.(e));
        };

        const hasValue = displayValue !== '';

        const iconPaddingClass = icon ? 'pl-10' : '';
        const rightPaddingClass = (type === 'password' || (clearable && hasValue)) ? 'pr-10' : '';

        const inputClassName = [
            [
                'border-border w-full rounded-lg border text-sm transition-colors outline-none focus:ring-1 focus:border-primary',
                'overflow-hidden text-ellipsis whitespace-nowrap placeholder:text-neutral-300',
            ].join(' '),
            sizeStyles[size],
            stateStyles[currentState],
            iconPaddingClass,
            rightPaddingClass,
            currentState !== 'disabled' ? className : undefined,
        ]
            .filter(Boolean)
            .join(' ');

        const renderedIcon = isValidElement(icon)
            ? (() => {
                const iconElement = icon as ReactElement<{ width?: number; height?: number }>;
                const { width = 20, height = 20 } = iconElement.props;
                return cloneElement(iconElement, { width, height });
            })()
            : icon;

        const isError = currentState === 'error';
        const errorId = isError && errorText ? `${inputId}-error` : undefined;

        return (
            <div className="flex flex-col gap-0.75 mx-[1px]">
                {label && (
                    <label htmlFor={inputId} className="text-primary text-xs font-normal">
                        {label}{requiredProp && <span className="text-error"> *</span>}
                    </label>
                )}
                <div className="relative w-full">
                    {icon && <div className="text-secondary pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">{renderedIcon}</div>}
                    <input
                        type={type === 'password' ? (showPassword ? 'text' : 'password') : type ?? 'text'}
                        {...props}
                        {...(allowAutocomplete ? {} : noAutocomplete)}
                        id={inputId}
                        className={inputClassName}
                        value={displayValue}
                        disabled={disabled}
                        onFocus={handleFocus}
                        onBlur={handleBlur}
                        onChange={handleChange}
                        ref={ref ?? inputRef}
                        data-testid={testId}
                        aria-invalid={isError || undefined}
                        aria-describedby={errorId}
                    />
                    {type !== 'password' && clearable && hasValue && !disabled && (
                        <button
                            type="button"
                            aria-label="Clear"
                            className="absolute top-1/2 right-3 -translate-y-1/2 text-secondary cursor-pointer"
                            data-testid={clearButtonTestId}
                            onClick={() => {
                                if (!isControlled) setInternalValue('');
                                onClear?.();
                            }}
                        >
                            <Close width={16} height={16} className="icon-stroke-thick" />
                        </button>
                    )}
                    {type === 'password' &&
                        (showPassword ? (
                            <EyeOff
                                width={16}
                                height={16}
                                className="icon-stroke-thick absolute top-1/2 right-3 -translate-y-1/2 text-secondary cursor-pointer"
                                data-testid={passwordToggleTestId}
                                onClick={() => {
                                    setShowPassword(false);
                                }}
                            />
                        ) : (
                            <Eye
                                width={16}
                                height={16}
                                className="icon-stroke-thick absolute top-1/2 right-3 -translate-y-1/2 text-secondary cursor-pointer"
                                data-testid={passwordToggleTestId}
                                onClick={() => {
                                    setShowPassword(true);
                                }}
                            />
                        ))}
                    {errorId && !errorInline && (
                        <p id={errorId} role="alert" className="absolute top-full left-0 text-error text-xs">{errorText}</p>
                    )}
                    {showCharCount && props.maxLength != null && (
                        <span className="absolute top-full right-0 text-secondary text-[10px]">
                            {displayValue.length} / {props.maxLength}
                        </span>
                    )}
                </div>
                {errorId && errorInline && (
                    <p id={errorId} role="alert" className="text-error text-xs mt-1">{errorText}</p>
                )}
            </div>
        );
    },
);
